import { NewsContext } from '@/context/NewsContext';
import { getImageUrl } from '@/utils/getimageurl';
import { ArrowRight } from 'lucide-react';
import { useContext, useState, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';

export const News = () => {
  const { news, loadingNews, errorNews } = useContext(NewsContext);
  const navigate = useNavigate();
  const location = useLocation();

  const safeNews = useMemo(() => (Array.isArray(news) ? news : []), [news]);

  // PAGE CHECK
  const isHome = location.pathname === '/';

  // PAGINATION STATE (only used on news page)
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 6;

  // SEARCH & SORT STATE
  const [searchQuery, setSearchQuery] = useState('');
  const [sortOrder, setSortOrder] = useState('desc'); // ✅ default newest first

  // FILTERED + SORTED NEWS
  // FILTERED + SORTED NEWS
  const filteredNews = useMemo(() => {
    let filtered = safeNews;

    // Filter by selected calendar date
    if (searchQuery) {
      filtered = filtered.filter((item) => {
        const newsDate = new Date(item.created_at).toISOString().split('T')[0]; // "YYYY-MM-DD"
        return newsDate === searchQuery; // compare with calendar input
      });
    }

    // Sort by created_at
    filtered.sort((a, b) => {
      const dateA = new Date(a.created_at).getTime();
      const dateB = new Date(b.created_at).getTime();
      return sortOrder === 'asc' ? dateA - dateB : dateB - dateA;
    });

    return filtered;
  }, [safeNews, searchQuery, sortOrder]);

  // HOME → only show first 3
  const homeNews = filteredNews.slice(0, 3);

  // NEWS PAGE PAGINATION
  const totalPages = Math.ceil(filteredNews.length / itemsPerPage);

  // Ensure currentPage never exceeds totalPages
  const safeCurrentPage = Math.min(currentPage, totalPages || 1);

  const paginatedNews = filteredNews.slice(
    (safeCurrentPage - 1) * itemsPerPage,
    safeCurrentPage * itemsPerPage
  );

  // FINAL LIST TO DISPLAY
  const displayedNews = isHome ? homeNews : paginatedNews;

  if (loadingNews) return <div>Loading News...</div>;
  if (errorNews) return <div>Error: {errorNews}</div>;

  return (
    <div className="mt-[3rem] flex w-full justify-center py-[4rem] font-inter md:mt-[3rem] lg:mt-[6rem]">
      {' '}
      <div className="flex w-full max-w-[114rem] flex-col items-stretch justify-center gap-[4rem] px-[2rem]">
        {/* ===== HEADING ===== */}
        {isHome && (
          <div className="flex flex-col items-center text-center">
            {' '}
            <h1 className="mb-[2rem] text-[1.8rem] font-[500] leading-[1.8rem] text-[#0EC9AC]">
              Our News{' '}
            </h1>{' '}
            <h1 className="text-[3.6rem] font-[600] leading-[4.65rem] text-white">
              Latest News{' '}
            </h1>{' '}
          </div>
        )}

        {!isHome && (
          <>
            <h1 className="mt-[4rem] w-full text-center text-[3.6rem] font-[600] leading-[4.65rem] text-white md:mt-[6rem]">
              Latest News
            </h1>
            <h1 className="mb-[2rem] mt-[-3rem] w-full text-center text-[1.8rem] font-[400] leading-[2.4rem] text-[#828FA0]">
              Stay updated with security-industry insights.
            </h1>
          </>
        )}
        {/* ===== SEARCH & SORT CONTROLS ===== */}
        {!isHome && (
          <div className="mb-[2rem] flex w-full flex-row items-center justify-end gap-[1rem]">
            <div className="flex w-[calc(100%-17rem)] flex-row flex-wrap gap-[0.6rem] md:w-[30rem]">
              <p className="w-full text-[1.4rem] text-white">Filter</p>
              <input
                type="date"
                value={searchQuery}
                onChange={(e) => {
                  setSearchQuery(e.target.value);
                  setCurrentPage(1); // reset pagination on search
                }}
                className="h-[4rem] w-full rounded-md border border-[white] bg-transparent px-[1rem] py-[0.5rem] text-[1.4rem] text-white opacity-[0.8] shadow-none placeholder:text-gray-400 [&::-webkit-calendar-picker-indicator]:!text-white"
              />
            </div>
            <div className="flex w-[15rem] flex-row flex-wrap gap-[0.6rem]">
              <p className="w-full text-[1.4rem] text-white">Sort</p>
              <select
                value={sortOrder}
                onChange={(e) => setSortOrder(e.target.value)}
                className="custom-select h-[4rem] w-[15rem] rounded-md border border-[white] bg-transparent px-[0.5rem] py-[0.5rem] pr-[2rem] text-[1.4rem] text-white opacity-[0.8] shadow-none"
              >
                <option
                  value="desc"
                  className="bg-transparent px-[0.5rem] py-[0.5rem] text-[1.5rem] text-white"
                >
                  Newest
                </option>
                <option
                  value="asc"
                  className="bg-transparent px-[0.5rem] py-[0.5rem] text-[1.5rem] text-white"
                >
                  Oldest
                </option>
              </select>
            </div>
          </div>
        )}
        {/* ===== NEWS GRID ===== */}
        <div className="grid w-full grid-cols-1 gap-[4rem] sm:grid-cols-2 md:grid-cols-2 lg:grid-cols-3">
          {displayedNews.map((item, index) => (
            <div
              key={index}
              className="flex flex-col items-center justify-start gap-3 rounded-xl text-center transition"
            >
              <img
                src={getImageUrl(item.banner_image)}
                alt={item.title}
                className="aspect-[352/250] w-full rounded-[1rem] object-cover"
              />
              <h3 className="mt-[0.5rem] w-full text-start text-[2rem] font-[600] leading-[2.6rem] text-white">
                {item.title}
              </h3>
              <h1 className="mt-[1.5rem] w-full text-start text-[1.2rem] font-[600] leading-[100%] text-[#999]">
                {new Date(item.created_at).toLocaleDateString('en-US', {
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric',
                })}
              </h1>
              <div
                onClick={() => navigate(`/news/${item.id}`)}
                className="mt-[2rem] flex w-full cursor-pointer items-center justify-start gap-[1rem] text-[#0EC9AC]"
              >
                <h1 className="text-[1.2rem] font-[600] leading-[100%]">READ MORE</h1>
                <ArrowRight className="h-auto w-[1.6rem]" />
              </div>
            </div>
          ))}
        </div>
        {/* ===== PAGINATION FOR NEWS PAGE ONLY ===== */}
        {!isHome && totalPages > 1 && (
          <>
            {/* ===== MOBILE PAGINATION (xs only) ===== */}
            <div className="mt-[3rem] flex w-full items-center justify-center gap-[1rem] sm:hidden">
              <button
                onClick={() => setCurrentPage((p) => Math.max(p - 1, 1))}
                className="rounded-lg border border-[#0EC9AC] bg-[#0EC9AC] px-[1rem] py-[0.5rem] text-[1.4rem] font-[600] text-white"
              >
                Prev
              </button>
              <span className="text-[1.4rem] font-[500] text-white">
                {safeCurrentPage} / {totalPages}
              </span>
              <button
                onClick={() => setCurrentPage((p) => Math.min(p + 1, totalPages))}
                className="rounded-lg border border-[#0EC9AC] bg-[#0EC9AC] px-[1rem] py-[0.5rem] text-[1.4rem] font-[600] text-white"
              >
                Next
              </button>
            </div>

            {/* ===== DESKTOP PAGINATION (sm and up) ===== */}
            <div className="mt-[3rem] hidden w-full justify-center gap-[1rem] sm:flex">
              <button
                onClick={() => setCurrentPage((p) => Math.max(p - 1, 1))}
                className="rounded-lg border border-[#0EC9AC] bg-[#0EC9AC] px-[1.4rem] py-[0.6rem] text-[1.6rem] font-[600] text-white"
              >
                Prev
              </button>

              {totalPages > 0 &&
                Array.from({ length: totalPages })
                  .map((_, i) => i + 1)
                  .filter((page) => {
                    const near = Math.abs(page - safeCurrentPage) <= 1;
                    const edges = page === 1 || page === totalPages;
                    return near || edges;
                  })
                  .map((page, idx, arr) => (
                    <div key={page} className="flex">
                      {idx > 0 && page !== arr[idx - 1] + 1 && (
                        <span className="px-2 text-[1.4rem] text-[#0EC9AC]">...</span>
                      )}
                      <button
                        onClick={() => setCurrentPage(page)}
                        className={`rounded-lg px-[1.4rem] py-[0.6rem] text-[1.6rem] font-[600] ${
                          safeCurrentPage === page
                            ? 'bg-[#0EC9AC] text-white'
                            : 'border border-[#0EC9AC] text-white'
                        }`}
                      >
                        {page}
                      </button>
                    </div>
                  ))}

              <button
                onClick={() => setCurrentPage((p) => Math.min(p + 1, totalPages))}
                className="rounded-lg border border-[#0EC9AC] bg-[#0EC9AC] px-[1.4rem] py-[0.6rem] text-[1.6rem] font-[600] text-white"
              >
                Next
              </button>
            </div>
          </>
        )}
        {/* ===== VIEW ALL BUTTON ON HOME ONLY ===== */}
        {isHome && safeNews.length > 3 && (
          <div className="flex justify-center">
            <button
              onClick={() => navigate('/news')}
              className="flex items-center gap-2 text-[1.4rem] font-[600] text-[#0EC9AC]"
            >
              View All News <ArrowRight />
            </button>
          </div>
        )}
      </div>
    </div>
  );
};
